/**
 * useCategoryTransactions Hook
 * 
 * Fetches transactions by type (expense/income) for usage checking.
 * Transactions are optional for category usage validation.
 */

import { useEffect, useState } from "react";
import { api } from "../../../services/api";
import { Transaction } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";

export interface UseCategoryTransactionsParams {
  workspaceId: string;
  type: "expense" | "income";
}

export interface UseCategoryTransactionsResult {
  transactions: Transaction[];
  loading: boolean;
}

/**
 * Hook for fetching transactions by type for usage checking
 */
export const useCategoryTransactions = ({ workspaceId, type }: UseCategoryTransactionsParams): UseCategoryTransactionsResult => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setTransactionsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!workspaceId || isDemoMode()) {
      setTransactions([]);
      setTransactionsLoading(false);
      return;
    }

    // Fetch transactions for the current tab type to check category usage
    const fetchTransactions = async (): Promise<void> => {
      setTransactionsLoading(true);
      try {
        const transactionType = type === "expense" ? "expense" : "income";
        
        // Use the centralized authenticated request helper for consistency
        const response = await api.makeAuthenticatedRequest(
          `/workspaces/${workspaceId}/transactions?type=${transactionType}`,
          { method: "GET" }
        );

        if (response.ok) {
          const data = await response.json();
          setTransactions(data);
        } else {
          // Log non-OK responses but don't throw - transactions are optional for usage checking
          console.warn(`Failed to fetch transactions: ${response.status} ${response.statusText}`);
          setTransactions([]);
        }
      } catch (err) {
        console.error("Error fetching transactions:", err);
        setTransactions([]);
        // Don't set error state, just log - transactions are optional for usage checking
      } finally {
        setTransactionsLoading(false);
      }
    };

    fetchTransactions();
  }, [workspaceId, type]);

  return {
    transactions,
    loading,
  };
};
